import { useReportEffect } from "../../../../hooks/useReportEffect";
import { ReportTop } from "../../../../components/Dashboard/ReportLayout";
import { FilterDateDashboard } from "../../../../components/Dashboard/ViewDatas/FilterDate";
import { ReactComponent as CattleMap } from "../../../../assets/svg/icons/cattle.svg";
import GraphBarLine, { CattleMapLegend } from "../../../../components/Dashboard/Graph/BarLine/CattleMap";

import { DateModal } from "../../../../components/Modal/DateFilter";
import { useFilter } from "../../../../hooks/useFilter";
import {
	selectionToNumericIds,
	selectionToSortedIdsKey,
	useFilterContextV2,
} from "../../../../contexts/FilterContextV2";
import Constants from "../../../../constants/Index";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRequestDateRange } from "../../../../utils/date/requestDateRange";
import { buildCapacitySeries, capacityRows } from "../../../../utils/reports/capacitySeries";
import { exportCattleMapDataToExcel } from "../../../../utils/excel/exportCattleMapData";
import { translateAnimalCategoryDescription } from "../../../../i18n/translateAnimalCategory";
import "../styles.css";
import { DebugSelectedAreaHectares } from "./DebugSelectedAreaHectares";

export default function DashboardCattleMapGeneral() {
  const { t, i18n } = useTranslation();
  const {
    setHideMicroAreaFilter,
    setHideBatchFilter,
    setRangeDays,
    rangeDays,
    setDashboard,
    balance,
    balanceAnimal,
    setBalanceAnimal,
    setBalance,
    setBalanceCapacity,
    setSideBarOptionSelected,
    setSelectRangeDate
  } = useFilter();

  const { selectedFarms, selectedAreas } = useFilterContextV2();

  const selectedFarmsFetchKey = useMemo(
    () => selectionToSortedIdsKey(selectedFarms),
    [selectedFarms],
  );

  const selectedAreasFetchKey = useMemo(
    () => selectionToSortedIdsKey(selectedAreas),
    [selectedAreas],
  );

  const [capacityData, setCapacityData] = useState<IBalanceCapacity[]>();
  const [dateFilterReady, setDateFilterReady] = useState(false);
  const capacityMonths = useMemo(() => {
    if (!capacityData || !rangeDays) return undefined;
    const end = rangeDays.to || new Date();
    const start = new Date(end.getFullYear(), end.getMonth() - 11, 1);
    return buildCapacitySeries(capacityData, start, end);
  }, [capacityData, rangeDays]);

  useEffect(() => {
    setHideMicroAreaFilter(true)
    setHideBatchFilter(true)
    setSideBarOptionSelected("DASHBOARD")
    setDashboard("CATTLE_MAP")
    setSelectRangeDate(2)
    document.title = Constants.TITLE_DASHBOARD;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    setRangeDays({ from: today, to: today });
    setDateFilterReady(true);
  }, []);

  useReportEffect((axios) => {
    setCapacityData(undefined);
    if (!dateFilterReady || !selectionToNumericIds(selectedFarms).length || !selectionToNumericIds(selectedAreas).length || !rangeDays) return;

    const controller = new AbortController();

    const fetchAllData = async () => {
      const requestParams = () => ({
        farmIds: selectionToNumericIds(selectedFarms),
        areaIds: selectionToNumericIds(selectedAreas),
        ...getRequestDateRange(rangeDays.from, rangeDays.to),
      });

      const paramsSerializer = (params: any) => new URLSearchParams(params).toString();

      try {
        const [balanceRes, animalRes, capacityRes] = await Promise.all([
          axios.get("/balance", {
            params: requestParams(),
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get("/balance/animal-weight-category", {
            params: requestParams(),
            paramsSerializer,
            signal: controller.signal
          }),
          axios.get<IBalanceCapacity[]>("/balance/capacity", {
            params: requestParams(),
            paramsSerializer,
            signal: controller.signal
          })
        ]);

        setBalance(balanceRes.data);
        setBalanceAnimal(animalRes.data);

        setBalanceCapacity(capacityRes.data);
        setCapacityData(capacityRes.data);

      } catch (error) {
        if (!controller.signal.aborted) {
          console.error('Erro ao carregar dados de balance:', error);
        }
      }
    };

    fetchAllData();

    return () => controller.abort();
  }, [dateFilterReady, selectedFarmsFetchKey, selectedAreasFetchKey, rangeDays]);

  return (
    <div className="cattleMap_background" style={{ height: "100vh", overflowY: "auto" }}>
      <DateModal />

      <form className="dashboardPastureManagement" >
        <div className="cattleMap_page">
          <ReportTop kind="cattle" icon={<CattleMap height={72} />} title={t("reports.cattleMap")} subtitle={t("reports.overview")}>
            <FilterDateDashboard
              onExportExcel={() => {
                if (balance && balanceAnimal && capacityMonths) {
                  try {
                    exportCattleMapDataToExcel(
                      balance,
                      balanceAnimal,
                      capacityMonths,
                      rangeDays?.from,
                      rangeDays?.to
                    );
                  } catch (error) {
                    console.error('Erro ao exportar:', error);
                    alert(t("reports.exportError"));
                  }
                }
              }}
            />
          </ReportTop>

          {balance != undefined &&
            <div className="tableDashboard" id={"saldoEstoque"}>
              <h2>{t("reports.balance")}</h2>
              <div style={{
                overflowX: "auto",
                width: "100%"
              }}>
                <table style={{
                  minWidth: "max-content",
                  width: "100%"
                }}>
                  <thead style={{
                    color: "#fff"
                  }}>
                    <th scope="col" rowSpan={2} style={{
                      background: "#324440",
                      minWidth: "150px",
                      textAlign: "left"
                    }}>
                      {t("reports.animalCategory")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#808080",
                      minWidth: "80px"
                    }}>
                      {t("reports.openingBalance")}
                    </th>
                    <th scope="col" colSpan={4} style={{
                      textAlign: "center",
                      background: "#62b8ae"
                    }}>
                      {t("reports.entry")}
                    </th>
                    <th scope="col" colSpan={4} style={{
                      textAlign: "center",
                      background: "#ff6161"
                    }}>
                      {t("reports.exit")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#808080",
                      minWidth: "80px"
                    }}>
                      {t("reports.evolution")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#324440",
                      minWidth: "80px"
                    }}>
                      {t("reports.periodAdjustment")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#57CE71",
                      minWidth: "80px"
                    }}>
                      {t("reports.balanceLabel")}
                    </th>
                    <tr>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.births")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.internalTransfer")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.externalTransfer")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.purchases")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.sales")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.internalTransfer")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"

                      }}>
                        {t("reports.externalTransfer")}
                      </th>
                      <th scope="col" style={{
                        background: "#324440",
                        minWidth: "80px"
                      }}>
                        {t("reports.deaths")}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {
                      balance.sort((a, b) => a.id - b.id)?.map((item, index) => (
                        <tr className={index % 2 === 0 ? " bg_white" : "bg_gray"}>
                          <th scope="row">{translateAnimalCategoryDescription(item)}</th>
                          <td>{item.opening_balance}</td>
                          <td>{item.birth}</td>
                          <td>{item.internal_in_transfer}</td>
                          <td>{item.external_in_transfer}</td>
                          <td>{item.shopping}</td>
                          <td>{item.sales}</td>
                          <td>{item.internal_out_transfer}</td>
                          <td>{item.external_out_transfer}</td>
                          <td>{item.death}</td>
                          <td>{item.evolution}</td>
                          <td>{item.inventory_adjustment}</td>
                          <td>{item.end_balance}</td>
                        </tr>
                      ))
                    }
                  </tbody>

                  <tfoot
                    style={{
                      background: "#324440",
                      color: "#fff"
                    }}
                  >
                    <th style={{
                      textAlign: "left"
                    }}>{t("reports.total")}</th>
                    <td>{balance?.reduce((acc, item) => acc + item.opening_balance, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.birth, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.internal_in_transfer, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.external_in_transfer, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.shopping, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.sales, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.internal_out_transfer, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.external_out_transfer, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.death, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.evolution, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.inventory_adjustment, 0)}</td>
                    <td>{balance?.reduce((acc, item) => acc + item.end_balance, 0)}</td>
                  </tfoot>
                </table>
              </div>
            </div>
          }

          {balanceAnimal != undefined &&
            <div className="tableDashboard" id={"categorias"}>
              <h2>{t("reports.animalCategoryWeightUa")}</h2>
              <div style={{
                overflowX: "auto",
                width: "100%"
              }}>
                <table style={{
                  minWidth: "max-content",
                  width: "100%"
                }}>
                  <thead style={{
                    color: "#fff"
                  }}>
                    <th scope="col" rowSpan={2} style={{
                      background: "#324440",
                      width: "150px",
                      textAlign: "left"
                    }}>
                      {t("reports.animalCategory")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#57CE71",
                      width: "80px"
                    }}>
                      {t("reports.finalStock")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#324440",
                      width: "100px"
                    }}>
                      {t("reports.averageWeightKg")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#808080",
                      width: "100px"
                    }}>
                      {t("reports.totalWeightKg")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#324440",
                      width: "100px"
                    }}>
                      {t("reports.totalArrobas")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#808080",
                      width: "80px"
                    }}>
                      {t("reports.totalUa")}
                    </th>
                    <th scope="col" rowSpan={2} style={{
                      background: "#324440",
                      width: "80px"
                    }}>
                      {t("reports.uaDistribution")}
                    </th>
                  </thead>

                  <tbody>
                    {
                      balanceAnimal
                        .reduce((acc: any, item) => {
                          const existingCategory = acc.find((cat: any) => cat.category_id === item.category_id);
                          if (existingCategory) {
                            existingCategory.quantity += item.quantity;
                            existingCategory.total_weight += item.total_weight;
                            existingCategory.total_arroba += item.total_arroba;
                            existingCategory.total_uas += item.total_uas;
                          } else {
                            acc.push({ ...item });
                          }
                          return acc;
                        }, [])
                        .sort((a: any, b: any) => a.category_id - b.category_id)
                        ?.map((item: IBalanceAnimal, index: number) => (
                          <tr className={index % 2 === 0 ? " bg_white" : "bg_gray"}>
                            <th scope="row">{translateAnimalCategoryDescription(item)}</th>
                            <td>{item.quantity}</td>
                            <td>{item.average_weight.toFixed(0)}</td>
                            <td>{item.total_weight}</td>
                            <td>{item.total_arroba}</td>
                            <td>{item.total_uas}</td>
                            <td>{parseFloat(Number((item.total_uas / balanceAnimal?.reduce((acc, item) => acc + item.total_uas, 0)) * 100).toFixed(2))} %</td>
                          </tr>
                        ))
                    }
                  </tbody>

                  <tfoot
                    style={{
                      background: "#324440",
                      color: "#fff"
                    }}
                  >
                    <th style={{
                      textAlign: "left"
                    }}>{t("reports.total")}</th>
                    <td>{balanceAnimal?.reduce((acc, item) => acc + item.quantity, 0)}</td>
                    <td></td>
                    <td>{balanceAnimal?.reduce((acc, item) => acc + item.total_weight, 0)}</td>
                    <td>{parseFloat(Number(balanceAnimal?.reduce((acc, item) => acc + item.total_arroba, 0)).toFixed(2))}</td>
                    <td>{parseFloat(Number(balanceAnimal?.reduce((acc, item) => acc + item.total_uas, 0)).toFixed(2))}</td>
                    <td />
                  </tfoot>
                </table>
              </div>
            </div>
          }

          {capacityMonths !== undefined &&
            <div className="tableDashboard capacity-report">
              <h2>{t("reports.stockAndStockingCurve")}</h2>
              <div className="capacity-report__content" style={{ overflowX: "auto", width: "100%" }}>
                <table style={{ minWidth: 1100, width: "100%" }}>
                  <colgroup>
                    <col style={{ width: 180 }} />
                    {capacityMonths.map(point => <col key={point.key} />)}
                  </colgroup>
                  <thead style={{ color: "#fff" }}>
                    <tr>
                      <th scope="col" style={{ background: "#324440" }}>
                        {t("reports.stockingMonth")}
                      </th>
                      {capacityMonths.map(point => (
                        <th key={point.key} scope="col" style={{
                          background: !point.data ? "#808080" : point.data.season === "DRY" ? "#D9B43C" : "#62B8AE",
                          height: 60,
                        }}>
                          <span>{new Intl.DateTimeFormat(i18n.language, { month: "short" })
                            .format(new Date(point.year, point.month - 1, 1))}</span>
                          <br />
                          <span>{point.year}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {capacityRows(capacityMonths).slice(0, 3).map(([label, ...values], index) => (
                      <tr key={label} className={index % 2 === 0 ? "bg_white" : "bg_gray"}>
                        <th scope="row">{label}</th>
                        {values.map((value, i) => <td key={capacityMonths[i].key}>{value}</td>)}
                      </tr>
                    ))}
                  </tbody>
                  {capacityMonths.length > 0 && <tfoot>
                    <tr>
                      <td className="capacity-chart__legend-cell"><CattleMapLegend /></td>
                      <td className="capacity-chart__plot-cell" colSpan={capacityMonths.length}>
                        <GraphBarLine points={capacityMonths} />
                      </td>
                    </tr>
                  </tfoot>}
                </table>
              </div>
              <DebugSelectedAreaHectares />
            </div>
          }

        </div>
      </form>
    </div>
  );
}
